import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Menu,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  SkipNext as SkipIcon,
  Notes as NotesIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  RateReview as ReviewIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';

interface WorkflowStep {
  step_index: number;
  step_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'stuck' | 'under_review';
  assigned_to?: number;
  assigned_to_name?: string;
  completed_by?: number;
  notes?: string;
  started_at?: string;
  completed_at?: string;
}

interface TemplateStep {
  name: string;
  description: string;
  owner_role: string;
  required: boolean;
  order: number;
  group_name?: string;
}

interface WorkflowData {
  id: number;
  job_id: number;
  template_id: number;
  template_name: string;
  template_type: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'cancelled';
  current_step_index: number;
  template_steps: TemplateStep[];
  step_progress: WorkflowStep[];
}

interface WorkflowChecklistProps {
  jobId: number | string;
  jobOwnerId?: number;
  currentUser?: any;
  canEdit: boolean;
  onUpdate?: () => void;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface StepGroup {
  name: string;
  steps: Array<{
    templateStep: TemplateStep;
    progressStep: WorkflowStep;
  }>;
  totalSteps: number;
  completedSteps: number;
  hasActiveStep: boolean;
}

const WorkflowChecklistGrouped: React.FC<WorkflowChecklistProps> = ({ 
  jobId, 
  jobOwnerId, 
  currentUser, 
  canEdit, 
  onUpdate 
}) => {
  const [workflow, setWorkflow] = useState<WorkflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStep, setUpdatingStep] = useState<number | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [users, setUsers] = useState<User[]>([]);
  const [assignmentDialog, setAssignmentDialog] = useState<{
    open: boolean;
    stepIndex: number | null;
    currentAssignee?: number;
  }>({
    open: false,
    stepIndex: null,
  });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedStepForStatus, setSelectedStepForStatus] = useState<number | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [notesDialog, setNotesDialog] = useState<{ 
    open: boolean; 
    stepIndex: number | null; 
    notes: string;
    requiredForStatus?: string;
    viewMode?: boolean;
  }>({
    open: false,
    stepIndex: null,
    notes: '',
    viewMode: false
  });

  // Fetch workflow data
  useEffect(() => {
    fetchWorkflow();
    fetchUsers();
  }, [jobId]);

  // Group steps by group_name
  const groupedSteps = useMemo(() => {
    if (!workflow) return [];

    const groups = new Map<string, StepGroup>();

    // Combine template steps with progress
    workflow.template_steps.forEach((templateStep, index) => {
      const progressStep = workflow.step_progress[index];
      const groupName = templateStep.group_name || 'Other';

      // Skip if no matching progress step
      if (!progressStep) {
        console.warn(`Missing progress step for template step ${index}: ${templateStep.name}`);
        return;
      }

      if (!groups.has(groupName)) {
        groups.set(groupName, {
          name: groupName,
          steps: [],
          totalSteps: 0,
          completedSteps: 0,
          hasActiveStep: false,
        });
      }

      const group = groups.get(groupName)!;
      group.steps.push({ templateStep, progressStep });
      group.totalSteps++;

      if (progressStep.status === 'completed' || progressStep.status === 'skipped') {
        group.completedSteps++;
      }

      if (progressStep.status === 'in_progress' || progressStep.status === 'under_review') {
        group.hasActiveStep = true;
      }
    });

    // Convert to array and determine which groups should be expanded by default
    const groupArray = Array.from(groups.values());
    
    // Auto-expand first incomplete group only on initial load
    if (!hasInitialized && workflow) {
      const firstActiveGroup = groupArray.find(group => 
        group.hasActiveStep || group.completedSteps < group.totalSteps
      );
      if (firstActiveGroup) {
        setExpandedGroups(new Set([firstActiveGroup.name]));
      }
      setHasInitialized(true);
    }

    return groupArray;
  }, [workflow, hasInitialized]);

  const fetchWorkflow = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/workflows/jobs/${jobId}/workflow`);
      
      if (response.data) {
        setWorkflow(response.data);
      }
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/workflows/eligible-users/${jobId}`);
      const usersData = response.data;
      
      // Ensure we have an array and add safety checks
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      } else {
        console.warn('Eligible users API returned non-array data:', usersData);
        setUsers([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch eligible users:', err);
      setUsers([]); // Set empty array on error
    }
  };

  const handleAssignStep = async (stepIndex: number, userId: number | null) => {
    if (!canEdit) return;

    try {
      setUpdatingStep(stepIndex);
      
      await api.put(`/workflows/jobs/${jobId}/workflow/steps/${stepIndex}/assign`, {
        assigned_to: userId
      });

      await fetchWorkflow();
      if (onUpdate) onUpdate();
      
      setAssignmentDialog({ open: false, stepIndex: null });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to assign step');
    } finally {
      setUpdatingStep(null);
    }
  };


  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleStepUpdate = async (stepIndex: number, newStatus: string, notes?: string) => {
    if (!canEditStep(workflow!.step_progress[stepIndex])) return;

    try {
      setUpdatingStep(stepIndex);
      
      await api.put(`/workflows/jobs/${jobId}/workflow/steps/${stepIndex}`, {
        status: newStatus,
        notes: notes || undefined
      });

      await fetchWorkflow();
      if (onUpdate) onUpdate();
      
      setError(null);
    } catch (err: any) {
      console.error('Workflow step update error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update step';
      setError(`Failed to update workflow step: ${errorMessage}`);
    } finally {
      setUpdatingStep(null);
      setNotesDialog({ open: false, stepIndex: null, notes: '', viewMode: false });
    }
  };


  const canEditStep = (step: WorkflowStep) => {
    const result = (() => {
      if (!canEdit || !currentUser) return false;
      
      // Admin can edit anything
      if (currentUser.role === 'admin') return true;
      
      // Job owner can edit their job's workflow
      if (jobOwnerId && currentUser.id === jobOwnerId) return true;
      
      // User can edit steps assigned to them
      if (step.assigned_to === currentUser.id) return true;
      
      // HR can manage all step assignments
      if (currentUser.role === 'hr') return true;
      
      return false;
    })();
    
    return result;
  };

  const handleChipClick = (progressStep: WorkflowStep) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (canEdit && canEditStep(progressStep) && updatingStep !== progressStep.step_index) {
      setStatusMenuAnchor(e.currentTarget as HTMLElement);
      setSelectedStepForStatus(progressStep.step_index);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'in_progress':
        return <ScheduleIcon color="primary" />;
      case 'skipped':
        return <SkipIcon color="action" />;
      case 'stuck':
        return <BlockIcon color="error" />;
      case 'under_review':
        return <ReviewIcon color="warning" />;
      default:
        return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'skipped': return 'default';
      case 'stuck': return 'error';
      case 'under_review': return 'warning';
      default: return 'default';
    }
  };

  const getGroupProgress = (group: StepGroup) => {
    return (group.completedSteps / group.totalSteps) * 100;
  };

  const getGroupStatus = (group: StepGroup) => {
    if (group.completedSteps === group.totalSteps) return 'completed';
    if (group.hasActiveStep) return 'in_progress';
    if (group.completedSteps > 0) return 'partial';
    return 'not_started';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!workflow) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No workflow assigned to this job yet.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box mb={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {workflow.template_name}
              </Typography>
              <Chip 
                label={workflow.status.replace('_', ' ')} 
                color={workflow.status === 'completed' ? 'success' : 'primary'}
                size="small"
              />
            </Stack>
            
            <LinearProgress 
              variant="determinate" 
              value={
                (workflow.step_progress.filter(s => 
                  s.status === 'completed' || s.status === 'skipped'
                ).length / workflow.step_progress.length) * 100
              }
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <List sx={{ p: 0 }}>
            {groupedSteps.map((group) => (
              <Box key={group.name} sx={{ mb: 2 }}>
                {/* Group Header */}
                <ListItemButton
                  onClick={() => toggleGroup(group.name)}
                  sx={{
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {expandedGroups.has(group.name) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {group.name}
                        </Typography>
                        <Chip
                          label={`${group.completedSteps} of ${group.totalSteps}`}
                          size="small"
                          color={getGroupStatus(group) === 'completed' ? 'success' : 'default'}
                        />
                      </Stack>
                    }
                    secondary={
                      <LinearProgress
                        variant="determinate"
                        value={getGroupProgress(group)}
                        sx={{ mt: 1, height: 4, borderRadius: 1 }}
                      />
                    }
                  />
                </ListItemButton>

                {/* Group Steps */}
                <Collapse in={expandedGroups.has(group.name)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 4 }}>
                    {group.steps.map(({ templateStep, progressStep }) => (
                      <ListItem
                        key={progressStep.step_index}
                        sx={{
                          pl: 2,
                          py: 2,
                          borderLeft: '3px solid',
                          borderLeftColor: progressStep.status === 'completed' ? 'success.main' : 'grey.300',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getStatusIcon(progressStep.status)}
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body1">
                                {templateStep.name}
                              </Typography>
                              <Chip
                                label={updatingStep === progressStep.step_index ? 'Updating...' : progressStep.status.replace('_', ' ')}
                                size="small"
                                color={getStatusColor(progressStep.status)}
                              />
                              {(() => {
                                const canEditResult = canEdit && canEditStep(progressStep);
                                const notUpdating = updatingStep !== progressStep.step_index;
                                const shouldShow = canEditResult && notUpdating;
                                
                                return shouldShow ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      setStatusMenuAnchor(e.currentTarget);
                                      setSelectedStepForStatus(progressStep.step_index);
                                    }}
                                    sx={{ ml: 1, minWidth: 'auto', px: 1 }}
                                  >
                                    Change
                                  </Button>
                                ) : null;
                              })()}
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {templateStep.description}
                              </Typography>
                              
                              {progressStep.assigned_to_name ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="body2">
                                    <strong>Assigned to:</strong> {progressStep.assigned_to_name}
                                  </Typography>
                                  {canEdit && (currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'manager' || currentUser?.id === jobOwnerId) && (
                                    <Tooltip title="Reassign step">
                                      <IconButton
                                        size="small"
                                        onClick={() => setAssignmentDialog({
                                          open: true,
                                          stepIndex: progressStep.step_index,
                                          currentAssignee: progressStep.assigned_to
                                        })}
                                        sx={{ ml: 0.5, p: 0.25 }}
                                      >
                                        <AssignmentIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              ) : (
                                canEdit && (currentUser?.role === 'admin' || currentUser?.role === 'hr' || currentUser?.role === 'manager' || currentUser?.id === jobOwnerId) && (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" color="text.secondary">
                                      Unassigned
                                    </Typography>
                                    <Tooltip title="Assign step">
                                      <IconButton
                                        size="small"
                                        onClick={() => setAssignmentDialog({
                                          open: true,
                                          stepIndex: progressStep.step_index,
                                          currentAssignee: progressStep.assigned_to
                                        })}
                                        sx={{ ml: 0.5, p: 0.25 }}
                                      >
                                        <AssignmentIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )
                              )}
                              
                              {progressStep.completed_at && (
                                <Typography variant="body2" color="text.secondary">
                                  Completed: {format(new Date(progressStep.completed_at), 'MMM d, yyyy')}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={1}>
                            {progressStep.notes && (
                              <Tooltip title="View notes">
                                <IconButton
                                  size="small"
                                  onClick={() => setNotesDialog({
                                    open: true,
                                    stepIndex: progressStep.step_index,
                                    notes: progressStep.notes || '',
                                    viewMode: true
                                  })}
                                >
                                  <NotesIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Notes Dialog */}
      <Dialog 
        open={notesDialog.open} 
        onClose={() => setNotesDialog({ open: false, stepIndex: null, notes: '', viewMode: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {notesDialog.viewMode ? 'Step Notes' : 'Add Notes'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={notesDialog.notes}
            onChange={(e) => setNotesDialog({ ...notesDialog, notes: e.target.value })}
            disabled={notesDialog.viewMode}
            placeholder="Add notes about this step..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          {!notesDialog.viewMode ? (
              <>
                <Button onClick={() => setNotesDialog({ open: false, stepIndex: null, notes: '', viewMode: false })}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (notesDialog.stepIndex !== null && notesDialog.requiredForStatus) {
                      handleStepUpdate(notesDialog.stepIndex, notesDialog.requiredForStatus, notesDialog.notes);
                    }
                  }}
                  variant="contained"
                >
                  Save
                </Button>
              </>
          ) : (
            <Button onClick={() => setNotesDialog({ open: false, stepIndex: null, notes: '', viewMode: false })}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog 
        open={assignmentDialog.open} 
        onClose={() => setAssignmentDialog({ open: false, stepIndex: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Step
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Assign to User</InputLabel>
            <Select
              value={assignmentDialog.currentAssignee || ''}
              onChange={(e) => setAssignmentDialog({ 
                ...assignmentDialog, 
                currentAssignee: e.target.value as number 
              })}
              label="Assign to User"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {Array.isArray(users) && users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialog({ open: false, stepIndex: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (assignmentDialog.stepIndex !== null) {
                handleAssignStep(
                  assignmentDialog.stepIndex, 
                  assignmentDialog.currentAssignee || null
                );
              }
            }}
            variant="contained"
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Selection Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => {
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}
      >
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            handleStepUpdate(selectedStepForStatus, 'pending');
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          📋 Pending
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            handleStepUpdate(selectedStepForStatus, 'in_progress');
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          ⏳ In Progress
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            handleStepUpdate(selectedStepForStatus, 'completed');
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          ✅ Completed
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            handleStepUpdate(selectedStepForStatus, 'skipped');
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          ⏭️ Skipped
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            setNotesDialog({
              open: true,
              stepIndex: selectedStepForStatus,
              notes: '',
              requiredForStatus: 'stuck',
              viewMode: false
            });
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          🚫 Stuck (requires note)
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedStepForStatus !== null) {
            handleStepUpdate(selectedStepForStatus, 'under_review');
          }
          setStatusMenuAnchor(null);
          setSelectedStepForStatus(null);
        }}>
          🔍 Under Review
        </MenuItem>
      </Menu>
    </>
  );
};

export default WorkflowChecklistGrouped;